import {
  AsYouType,
  getCountries,
  getCountryCallingCode,
  parsePhoneNumberFromString,
  type CountryCode,
} from 'libphonenumber-js';

export const DEFAULT_PHONE_COUNTRY: CountryCode = 'US';

export type PhoneCountryOption = { code: CountryCode; dial: string; label: string };

export const PHONE_COUNTRY_OPTIONS: PhoneCountryOption[] = (() => {
  const all = getCountries()
    .map((code) => ({
      code,
      dial: getCountryCallingCode(code),
      label: `${code} +${getCountryCallingCode(code)}`,
    }))
    .sort((a, b) =>
      a.dial === b.dial ? a.code.localeCompare(b.code) : Number(a.dial) - Number(b.dial),
    );
  const usIdx = all.findIndex((o) => o.code === 'US');
  if (usIdx <= 0) return all;
  const us = all[usIdx]!;
  const rest = all.filter((_, i) => i !== usIdx);
  return [us, ...rest];
})();

/** Prefer E.164; fall back to +country+digits so optional fields still persist partial input clearly. */
export function normalizePhoneToE164(country: CountryCode, nationalInput: string): string | null {
  const digits = nationalInput.replace(/\D/g, '');
  if (!digits) return null;
  const parsed =
    parsePhoneNumberFromString(nationalInput, country) ?? parsePhoneNumberFromString(digits, country);
  if (parsed?.isValid()) return parsed.format('E.164');
  if (parsed?.isPossible()) return parsed.format('E.164');
  const cc = getCountryCallingCode(country);
  return `+${cc}${digits}`;
}

/**
 * Admin / read-only display: `US +1 (757) 432-4566` when parseable; best-effort for legacy bare digits.
 */
export function formatPhoneForDisplay(phone: string | null | undefined): string {
  if (!phone?.trim()) return '';
  const raw = phone.trim();
  let parsed = parsePhoneNumberFromString(raw);
  if (!parsed) parsed = parsePhoneNumberFromString(raw, DEFAULT_PHONE_COUNTRY) ?? undefined;
  if (parsed?.isValid()) {
    const iso = parsed.country ?? '??';
    return `${iso} +${parsed.countryCallingCode} ${parsed.formatNational()}`;
  }

  const digits = raw.replace(/\D/g, '');
  if (!digits) return raw;

  if (!raw.startsWith('+')) {
    const usTry = parsePhoneNumberFromString(digits, DEFAULT_PHONE_COUNTRY);
    if (usTry?.isValid()) {
      return `${DEFAULT_PHONE_COUNTRY} +${usTry.countryCallingCode} ${usTry.formatNational()}`;
    }
    const partial = new AsYouType(DEFAULT_PHONE_COUNTRY).input(digits);
    if (partial) return `${DEFAULT_PHONE_COUNTRY} +${getCountryCallingCode(DEFAULT_PHONE_COUNTRY)} ${partial}`;
  }

  const intl = parsePhoneNumberFromString('+' + digits);
  if (intl?.isValid()) {
    const iso = intl.country ?? '??';
    return `${iso} +${intl.countryCallingCode} ${intl.formatNational()}`;
  }

  return raw;
}

export function dialHrefFromPhone(phone: string | null | undefined): string | null {
  if (!phone?.trim()) return null;
  const raw = phone.trim();
  let parsed = parsePhoneNumberFromString(raw);
  if (!parsed) parsed = parsePhoneNumberFromString(raw, DEFAULT_PHONE_COUNTRY) ?? undefined;
  if (parsed?.isValid()) return `tel:${parsed.format('E.164')}`;

  const digits = raw.replace(/\D/g, '');
  if (!digits) return null;

  if (!raw.startsWith('+')) {
    const us = parsePhoneNumberFromString(digits, DEFAULT_PHONE_COUNTRY);
    if (us?.isValid()) return `tel:${us.format('E.164')}`;
    if (digits.length >= 9 && digits.length <= 10) {
      return `tel:+1${digits}`;
    }
  }

  const intl = parsePhoneNumberFromString('+' + digits);
  if (intl?.isValid()) return `tel:${intl.format('E.164')}`;

  if (digits.length >= 8 && digits.length <= 15) {
    return `tel:+${digits}`;
  }

  return null;
}
